import { DayPicker, getDefaultClassNames, type DayPickerProps } from 'react-day-picker'
import { enUS, ptBR } from 'react-day-picker/locale'
import { ArrowLeftIcon, ArrowRightIcon } from '@tractian/icons'

function join(...parts: Array<string | undefined>) {
  return parts.filter(Boolean).join(' ')
}

export function resolveCalendarLocale(language?: string) {
  return language === 'pt-BR' ? ptBR : enUS
}

// shadcn Calendar: react-day-picker v9 restyled with the repo's Tailwind tokens.
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: DayPickerProps) {
  const defaults = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={join('p-3', className)}
      classNames={{
        root: join(defaults.root, 'w-fit'),
        months: join(defaults.months, 'relative flex flex-col gap-4 sm:flex-row sm:gap-6'),
        month: join(defaults.month, 'flex flex-col gap-3'),
        nav: join(defaults.nav, 'absolute inset-x-0 top-0 flex items-center justify-between'),
        button_previous: join(
          defaults.button_previous,
          'flex size-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100'
        ),
        button_next: join(
          defaults.button_next,
          'flex size-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100'
        ),
        month_caption: join(defaults.month_caption, 'flex h-7 items-center justify-center'),
        caption_label: join(defaults.caption_label, 'text-sm font-medium text-gray-900'),
        month_grid: 'w-full border-collapse',
        weekdays: join(defaults.weekdays, 'flex'),
        weekday: join(defaults.weekday, 'size-9 text-xs font-normal leading-9 text-gray-500'),
        week: 'mt-1 flex w-full',
        day: join(defaults.day, 'relative size-9 p-0 text-center text-sm'),
        day_button: join(
          defaults.day_button,
          'size-9 rounded-md text-gray-900 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
        ),
        today: join(defaults.today, 'font-semibold text-primary'),
        outside: join(defaults.outside, 'text-gray-400'),
        disabled: join(defaults.disabled, 'opacity-40'),
        range_start: join(defaults.range_start, 'rounded-l-md bg-primary/10'),
        range_middle: join(
          defaults.range_middle,
          'bg-primary/10 [&>button]:!bg-transparent [&>button]:!text-gray-900'
        ),
        range_end: join(defaults.range_end, 'rounded-r-md bg-primary/10'),
        selected: join(
          defaults.selected,
          '[&>button]:bg-primary [&>button]:text-white [&>button:hover]:bg-primary'
        ),
        ...classNames
      }}
      components={{
        Chevron: ({ orientation, className: iconClass }) =>
          orientation === 'left' ? (
            <ArrowLeftIcon className={join('size-4', iconClass)} />
          ) : (
            <ArrowRightIcon className={join('size-4', iconClass)} />
          )
      }}
      {...props}
    />
  )
}
